import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { Button, Toggle, Loading, Dropdown } from '../../../../common/ui';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import { findOrderSettings, updateOrderSettings } from '../actions';

const ORDER_ROLLOUT_PERIOD_OPTIONS = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'UNTIL_MAX_HITS', label: 'Until max hits' },
];

const OrderSettings = memo(
  ({
    orderSettingsState: { orderSettingsData },
    showAlert,
    findOrderSettings,
    updateOrderSettings,
  }) => {
    const originalDataRef = useRef({});
    const [orderSettingsForm, setOrderSettingsForm] = useState({
      'is-ordering-enabled': '',
      'is-bill-item-detail-collect-popup-enabled': '',
      'order-rollout-period': 'DAILY',
    });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
      findOrderSettings();
    }, []);

    useEffect(() => {
      if (
        Array.isArray(orderSettingsData) &&
        orderSettingsData.length > 0
      ) {
        const nextForm = {
          'is-ordering-enabled': '',
          'is-bill-item-detail-collect-popup-enabled': '',
          'order-rollout-period': 'DAILY',
        };
        for (const item of orderSettingsData) {
          const paramKey = item?.param;
          const paramValue = item?.value;
          if (
            paramKey &&
            Object.prototype.hasOwnProperty.call(nextForm, paramKey)
          ) {
            nextForm[paramKey] = paramValue ?? '';
          }
        }
        originalDataRef.current = { ...nextForm };
        setOrderSettingsForm(nextForm);
      }
    }, [orderSettingsData]);

    const handleToggleChange = (e) => {
      const { name, checked } = e.target;
      setOrderSettingsForm((prev) => ({
        ...prev,
        [name]: checked ? 'true' : 'false',
      }));
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      const changedFields = {};
      Object.keys(orderSettingsForm).forEach((key) => {
        const currentVal = orderSettingsForm[key] ?? '';
        const originalVal = originalDataRef.current[key] ?? '';
        if (currentVal !== originalVal) {
          changedFields[key] = currentVal;
        }
      });

      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        setIsLoading(false);
        return;
      }

      const orderSettings = {
        orderSettingsData: changedFields,
      };
      const result = await updateOrderSettings(orderSettings);
      if (result) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Order settings updated successfully!',
        });
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Failed to update order settings!',
        });
      }
      setIsLoading(false);
      findOrderSettings();
    };

    const onClickCancel = () => {
      navigate('/');
    };

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Order Settings</h1>
            <p className={styles.breadcrumb}>Settings / Order</p>
          </div>
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            {isLoading && <Loading variant="overlay" label="" />}
            <form onSubmit={onSubmit} noValidate>
              <div className={styles.topDiv}>
                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>General Order Settings</h2>
                  <p className={styles.topicCaption}>
                    Here you can change the general order settings
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={orderSettingsForm['is-ordering-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    name="is-ordering-enabled"
                    label="Ordering Enable"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={
                      orderSettingsForm[
                        'is-bill-item-detail-collect-popup-enabled'
                      ] === 'true'
                    }
                    onChange={handleToggleChange}
                    name="is-bill-item-detail-collect-popup-enabled"
                    label="Bill item detail popup"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Dropdown
                    id="order-rollout-period"
                    label="Order Number Rollout Period"
                    value={orderSettingsForm['order-rollout-period']}
                    options={ORDER_ROLLOUT_PERIOD_OPTIONS}
                    placeholder="Select order number rollout period"
                    onChange={(value) =>
                      setOrderSettingsForm((prev) => ({
                        ...prev,
                        'order-rollout-period': value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className={styles.buttonPanelContainer}>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={onClickCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  auth: state.authState,
  orderSettingsState: state.orderSettingsState,
});

export default connect(mapStateToProps, {
  showAlert,
  updateOrderSettings,
  findOrderSettings,
})(OrderSettings);
